import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import API_BASE from '../config';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'proposal', label: 'Proposals' },
  { key: 'invoice', label: 'Invoices' },
  { key: 'cancellation', label: 'Cancellations' },
];

const TYPE_BADGE = {
  proposal: { bg: '#EBF8FF', text: '#2B6CB0' },
  invoice: { bg: '#F0FFF4', text: '#276749' },
  agreement: { bg: '#F5F5F5', text: '#555' },
};

const STATUS_BADGE = {
  sent: { bg: '#FFF8E6', text: '#B7791F' },
  paid: { bg: '#F0FFF4', text: '#276749' },
  cancelled: { bg: '#FFF5F5', text: '#8B1A1A' },
  failed: { bg: '#FFF5F5', text: '#C53030' },
};

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtAmount(n) {
  if (!n) return '—';
  return `£${Number(n).toLocaleString('en-GB')}`;
}

export default function Documents() {
  const { getToken } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    getToken().then(token =>
      fetch(`${API_BASE}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json())
    ).then(data => {
      setDocs(Array.isArray(data) ? data : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleDownload(doc) {
    setDownloading(prev => ({ ...prev, [doc.id]: true }));
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/documents/${doc.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || 'document.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    }
    setDownloading(prev => ({ ...prev, [doc.id]: false }));
  }

  const visible = filter === 'all' ? docs : docs.filter(d => d.type === filter);

  const btnStyle = (active) => ({
    fontSize: '0.8rem',
    padding: '0.35rem 0.9rem',
    border: active ? 'none' : '1px solid #D4CFC9',
    borderRadius: '5px',
    cursor: 'pointer',
    background: active ? '#8B1A1A' : 'transparent',
    color: active ? '#fff' : '#555',
    fontWeight: active ? '600' : '400',
  });

  return (
    <div style={{ padding: '2rem', maxWidth: '1100px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1A1A1A', margin: '0 0 0.25rem' }}>Documents</h1>
      <p style={{ fontSize: '0.875rem', color: '#888', margin: '0 0 1.5rem' }}>Full audit trail of all proposals, invoices and agreements sent.</p>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={btnStyle(filter === f.key)}>{f.label}</button>
        ))}
      </div>

      {loading && <p style={{ fontSize: '0.875rem', color: '#888' }}>Loading…</p>}

      {!loading && visible.length === 0 && (
        <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '2rem', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#888' }}>No documents sent yet.</p>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E8E4E0', background: '#FAFAF8' }}>
                {['Type', 'Client', 'Sent to', 'Sent by', 'Date & time', 'Amount', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '0.65rem 1rem', textAlign: 'left', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((doc, i) => {
                const typeBadge = TYPE_BADGE[doc.type] || TYPE_BADGE.agreement;
                const statusBadge = STATUS_BADGE[doc.status] || STATUS_BADGE.sent;
                return (
                  <tr key={doc.id} style={{ borderBottom: i < visible.length - 1 ? '0.5px solid #F0EDE8' : 'none' }}>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ fontSize: '0.75rem', background: typeBadge.bg, color: typeBadge.text, padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: '500', textTransform: 'capitalize' }}>{doc.type}</span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#1A1A1A', fontWeight: '500' }}>{doc.client_name || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#555' }}>{doc.contact_email || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#555' }}>{doc.sent_by_email || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#555', whiteSpace: 'nowrap' }}>{fmtDate(doc.sent_at)}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#1A1A1A' }}>{fmtAmount(doc.amount_gbp)}/mo</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ fontSize: '0.75rem', background: statusBadge.bg, color: statusBadge.text, padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: '500', textTransform: 'capitalize' }}>{doc.status}</span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                          onClick={() => handleDownload(doc)}
                          disabled={downloading[doc.id]}
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.65rem', background: '#1A1A1A', color: '#fff', border: 'none', borderRadius: '4px', cursor: downloading[doc.id] ? 'default' : 'pointer', opacity: downloading[doc.id] ? 0.6 : 1 }}
                        >
                          {downloading[doc.id] ? 'Downloading…' : 'Download PDF'}
                        </button>
                        {doc.stripe_session_id && (
                          <a
                            href={`https://dashboard.stripe.com/payments/${doc.stripe_session_id}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: '0.75rem', color: '#635BFF', textDecoration: 'none' }}
                          >
                            Stripe →
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
